(() => {
  'use strict';

  const ROOT = '[data-zp-guide]';
  const EPSILON = 1;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const cssLength = (value, width) => {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return 0;
    return String(value).trim().endsWith('%') ? number * width / 100 : number;
  };
  function stepScrollTarget({ scrollLeft, trackLeft, clientLeft = 0, paddingLeft = 0, maxScroll }, stepLeft, marginLeft = 0) {
    return clamp(scrollLeft + stepLeft - trackLeft - clientLeft - paddingLeft - marginLeft, 0, Math.max(0, maxScroll));
  }
  function nearestStepIndex(targets, scrollLeft, preferred = -1) {
    if (!Array.isArray(targets) || !targets.length || !Number.isFinite(scrollLeft) || targets.some(value => !Number.isFinite(value))) return -1;
    const distances = targets.map(target => Math.abs(target - scrollLeft));
    const distance = Math.min(...distances);
    const candidates = distances.map((value, index) => value <= distance + EPSILON ? index : -1).filter(index => index >= 0);
    if (candidates.includes(preferred)) return preferred;
    if (scrollLeft >= Math.max(...targets) - EPSILON && Math.max(...targets) > Math.min(...targets) + EPSILON) {
      return candidates[candidates.length - 1];
    }
    return candidates[0];
  }

  function createSteps(doc, win) {
    const instances = new Map();
    let started = false;
    const rootsWithin = (scope = doc) => [
      ...(scope.matches?.(ROOT) ? [scope] : []),
      ...Array.from(scope.querySelectorAll?.(ROOT) || [])
    ];
    const restoreAttribute = (element, name, value) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    };
    function initRoot(root) {
      if (instances.has(root)) return instances.get(root);
      const track = root.querySelector('[data-guide-track]');
      const controls = root.querySelector('[data-guide-controls]');
      if (controls) controls.hidden = true;
      if (!root.id || !track || !controls || track.closest(ROOT) !== root || controls.closest(ROOT) !== root) return null;
      const steps = Array.from(track.querySelectorAll('[data-guide-step]'));
      const buttons = Array.from(controls.querySelectorAll('[data-guide-select]'));
      const previous = controls.querySelector('[data-guide-prev]');
      const next = controls.querySelector('[data-guide-next]');
      const status = controls.querySelector('[data-guide-status]');
      if (track.tagName !== 'OL' || steps.length < 2 || buttons.length !== steps.length
        || !previous || !next || !status || typeof track.scrollTo !== 'function') return null;
      if (previous.tagName !== 'BUTTON' || next.tagName !== 'BUTTON' || previous === next) return null;
      const orderedButtons = [];
      const ids = new Set();
      for (const step of steps) {
        if (step.tagName !== 'LI' || step.parentElement !== track || !step.id || ids.has(step.id)) return null;
        ids.add(step.id);
      }
      for (const button of buttons) {
        const value = button.getAttribute('data-guide-select');
        if (button.tagName !== 'BUTTON' || !/^(0|[1-9][0-9]*)$/.test(value || '')) return null;
        const index = Number(value);
        if (index >= steps.length || orderedButtons[index] || button === previous || button === next
          || button.getAttribute('aria-controls') !== steps[index].id) return null;
        orderedButtons[index] = button;
      }
      if (orderedButtons.length !== steps.length || orderedButtons.some(button => !button)) return null;
      const idCounts = new Map();
      for (const element of doc.querySelectorAll('[id]')) idCounts.set(element.id, (idCounts.get(element.id) || 0) + 1);
      if (idCounts.get(root.id) !== 1 || steps.some(step => idCounts.get(step.id) !== 1)) return null;

      const allButtons = [...orderedButtons, previous, next];
      const original = {
        types: allButtons.map(button => button.getAttribute('type')),
        current: orderedButtons.map(button => button.getAttribute('aria-current')),
        previousDisabled: previous.disabled, nextDisabled: next.disabled,
        status: status.textContent, live: status.getAttribute('aria-live')
      };
      const state = { root, track, controls, steps, buttons: orderedButtons, current: -1, frame: null, pending: null, listeners: [], observer: null };
      const listen = (target, name, callback, options) => {
        target.addEventListener(name, callback, options);
        state.listeners.push(() => target.removeEventListener(name, callback, options));
      };
      const measure = () => {
        const rectangle = track.getBoundingClientRect();
        const style = win.getComputedStyle?.(track);
        const metrics = {
          scrollLeft: track.scrollLeft, trackLeft: rectangle.left,
          clientLeft: track.clientLeft || 0,
          paddingLeft: cssLength(style?.scrollPaddingLeft, track.clientWidth),
          maxScroll: Math.max(0, track.scrollWidth - track.clientWidth)
        };
        return {
          scrollLeft: metrics.scrollLeft,
          targets: steps.map(step => stepScrollTarget(metrics, step.getBoundingClientRect().left,
            cssLength(win.getComputedStyle?.(step)?.scrollMarginLeft, track.clientWidth)))
        };
      };
      const paint = (index) => {
        if (index < 0 || index >= steps.length || index === state.current) return;
        state.current = index;
        orderedButtons.forEach((button, itemIndex) => {
          if (itemIndex === index) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
        previous.disabled = index === 0;
        next.disabled = index === steps.length - 1;
        status.textContent = 'Step ' + (index + 1) + ' of ' + steps.length;
      };
      const sync = () => {
        if (instances.get(root) !== state) return;
        const geometry = measure();
        if (state.pending) {
          if (Math.abs(geometry.scrollLeft - geometry.targets[state.pending.index]) <= EPSILON) state.pending = null;
          else return;
        }
        paint(nearestStepIndex(geometry.targets, geometry.scrollLeft, state.current));
      };
      const queueSync = () => {
        if (instances.get(root) !== state || state.frame !== null) return;
        state.frame = win.requestAnimationFrame(() => { state.frame = null; sync(); });
      };
      const select = (index, focusButton = false) => {
        const destination = clamp(index, 0, steps.length - 1);
        const geometry = measure();
        const left = geometry.targets[destination];
        paint(destination);
        state.pending = Math.abs(track.scrollLeft - left) > EPSILON ? { index: destination } : null;
        if (focusButton) orderedButtons[destination].focus({ preventScroll: true });
        track.scrollTo({
          left, top: track.scrollTop,
          behavior: win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      };
      const selectedButton = target => {
        const element = target?.closest ? target : target?.parentElement;
        return element?.closest('[data-guide-select]');
      };
      const onClick = event => {
        if (event.defaultPrevented || (event.button !== undefined && event.button !== 0)) return;
        const button = selectedButton(event.target);
        const index = orderedButtons.indexOf(button);
        if (index >= 0) select(index);
        else if (previous.contains(event.target) && !previous.disabled) select(state.current - 1);
        else if (next.contains(event.target) && !next.disabled) select(state.current + 1);
      };
      const onKey = event => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
          || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const index = orderedButtons.indexOf(selectedButton(event.target));
        if (index < 0 && event.target !== track && event.target !== previous && event.target !== next) return;
        const current = index >= 0 ? index : state.current;
        const destination = event.key === 'Home' ? 0 : event.key === 'End' ? steps.length - 1
          : current + (event.key === 'ArrowLeft' ? -1 : 1);
        event.preventDefault();
        select(destination, index >= 0);
      };
      const manualIntent = () => { state.pending = null; };
      state.geometryChanged = () => { state.pending = null; queueSync(); };
      allButtons.forEach(button => button.setAttribute('type', 'button'));
      status.setAttribute('aria-live', 'polite');
      listen(controls, 'click', onClick);
      listen(controls, 'keydown', onKey);
      listen(track, 'keydown', onKey);
      listen(track, 'scroll', queueSync, { passive: true });
      listen(track, 'scrollend', () => { state.pending = null; queueSync(); }, { passive: true });
      for (const name of ['pointerdown', 'touchstart', 'wheel']) listen(track, name, manualIntent, { passive: true });
      state.cleanup = () => {
        if (state.frame !== null) win.cancelAnimationFrame(state.frame);
        state.pending = null;
        state.listeners.forEach(remove => remove());
        state.observer?.disconnect();
        allButtons.forEach((button, index) => restoreAttribute(button, 'type', original.types[index]));
        orderedButtons.forEach((button, index) => restoreAttribute(button, 'aria-current', original.current[index]));
        previous.disabled = original.previousDisabled;
        next.disabled = original.nextDisabled;
        status.textContent = original.status;
        restoreAttribute(status, 'aria-live', original.live);
        controls.hidden = true;
      };
      instances.set(root, state);
      sync();
      controls.hidden = false;
      if (win.ResizeObserver) {
        state.observer = new win.ResizeObserver(state.geometryChanged);
        state.observer.observe(track);
        steps.forEach(step => state.observer.observe(step));
      }
      return state;
    }
    const init = (scope = doc) => rootsWithin(scope).map(initRoot);
    const destroy = root => {
      const state = instances.get(root);
      if (!state) return;
      state.cleanup();
      instances.delete(root);
    };
    const onReady = () => init(doc);
    const onLoad = event => { rootsWithin(event.target).forEach(destroy); init(event.target); };
    const onUnload = event => rootsWithin(event.target).forEach(destroy);
    const onResize = () => instances.forEach(state => state.geometryChanged());
    function start() {
      if (started) return;
      started = true;
      doc.addEventListener('shopify:section:load', onLoad);
      doc.addEventListener('shopify:section:unload', onUnload);
      win.addEventListener('resize', onResize, { passive: true });
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', onReady, { once: true });
      else init(doc);
    }
    function stop() {
      doc.removeEventListener('DOMContentLoaded', onReady);
      doc.removeEventListener('shopify:section:load', onLoad);
      doc.removeEventListener('shopify:section:unload', onUnload);
      win.removeEventListener('resize', onResize);
      Array.from(instances.keys()).forEach(destroy);
      started = false;
    }
    return { init, destroy, start, stop };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { stepScrollTarget, nearestStepIndex, createSteps };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ziqingStepsController) { window.__ziqingStepsController.init(document); return; }
  const controller = createSteps(document, window);
  window.__ziqingStepsController = controller;
  controller.start();
})();
